'use client';
import React from 'react';
import { Brain, Target, MessageSquare, Users, Sparkles } from 'lucide-react';

const differentiators = [
  {
    icon: Brain,
    title: 'AI remembers your business.',
    desc: 'Every client. Every conversation. Every promise. AgentOS builds context across your entire business — and never forgets.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.08)',
    border: 'rgba(0,212,255,0.20)',
  },
  {
    icon: Target,
    title: 'AI tells you what matters most.',
    desc: "Stop guessing. AgentOS surfaces your highest-priority opportunities every morning — so you always know where to focus.",
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.08)',
    border: 'rgba(167,139,250,0.20)',
  },
  {
    icon: MessageSquare,
    title: 'AI explains every recommendation.',
    desc: 'No black boxes. No mystery. AgentOS tells you exactly why it\'s suggesting something — so you can make confident decisions.',
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.08)',
    border: 'rgba(34,197,94,0.20)',
  },
  {
    icon: Users,
    title: 'AI works with you — not instead of you.',
    desc: 'AgentOS drafts. You decide. You send. The AI amplifies your judgment — it never replaces it.',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.20)',
  },
];

export default function WhyAgentOSDifferentSection() {
  return (
    <section id="why-different" className="py-24 relative overflow-hidden" style={{ background: '#0D1120' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(167,139,250,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            What Sets Us Apart
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Why AgentOS{' '}
            <span className="gradient-cyan-lavender-text">Is Different.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Four principles that define how AgentOS works — and why it feels unlike anything you've used before.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {differentiators.map((item, index) => (
            <div
              key={item.title}
              className="rounded-2xl p-8 flex gap-6 card-hover cursor-default"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = item.bg;
                (e.currentTarget as HTMLElement).style.borderColor = item.border;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
              }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: item.bg, border: `1px solid ${item.border}` }}
              >
                <item.icon size={24} style={{ color: item.color }} />
              </div>
              <div>
                <div
                  className="text-xs font-bold mb-2"
                  style={{ color: item.color, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}
                >
                  0{index + 1}
                </div>
                <h3 className="text-lg font-extrabold mb-2 leading-snug" style={{ color: '#F0F4FF' }}>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
